"use client"

import * as React from "react"
import { AlertTriangleIcon, Building2Icon, CheckIcon, ChevronRightIcon, GitBranchIcon, PlusIcon } from "lucide-react"

import { useShowMultiBranchWorkspaceUi } from "@/features/auth/hooks/use-show-multi-branch-workspace-ui"
import { RegisterChurchDialog } from "@/features/create-church"
import { WorkspaceAvatar } from "@/features/dashboard/components/workspace-avatar"
import { useTenantContextSwitching } from "@/features/dashboard/hooks/use-tenant-context-switching"
import { useTranslations } from "@/i18n"
import { isParishBranchAlias } from "@/shared/lib/branch/branch-admin-routing"
import { cn } from "@/shared/lib/utils"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  triggerHaptic,
} from "@ibada/ui"

const rowClassName =
  "flex min-h-14 w-full items-center gap-3 px-3 py-2.5 text-left text-[15px] transition-colors active:bg-muted focus-visible:outline-none focus-visible:bg-muted"

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className='px-1 text-xs font-bold tracking-wide text-muted-foreground uppercase'>{children}</h3>
}

/**
 * What tapping the church name in the phone header opens: one bottom sheet with the branches (multi-branch
 * only, organization scope first), the churches this user belongs to, and the door to registering a separate
 * church. Same scopes and landing as the desktop switcher ({@link useTenantContextSwitching}).
 */
export function useMobileContextSwitcher() {
  const t = useTranslations("churchNav.switcher")
  const tHeader = useTranslations("dashboard.mobileHome")
  const showMultiBranchUi = useShowMultiBranchWorkspaceUi()
  const { workspaceSlug, branchSlug, workspaceOptions, branchOptions, switchWorkspace, switchBranch } =
    useTenantContextSwitching()
  const [sheetOpen, setSheetOpen] = React.useState(false)
  const [registerChurchOpen, setRegisterChurchOpen] = React.useState(false)

  const canSwitchBranch = showMultiBranchUi && branchOptions.length > 1
  const orderedBranchOptions = React.useMemo(
    () => [
      ...branchOptions.filter((option) => isParishBranchAlias(option.value)),
      ...branchOptions.filter((option) => !isParishBranchAlias(option.value)),
    ],
    [branchOptions],
  )

  const open = React.useCallback(() => {
    triggerHaptic("light")
    setSheetOpen(true)
  }, [])

  const choose = (action: () => void) => {
    triggerHaptic("light")
    setSheetOpen(false)
    action()
  }

  const element = (
    <>
      <Drawer open={sheetOpen} onOpenChange={setSheetOpen}>
        <DrawerContent>
          <DrawerHeader className='group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left'>
            <DrawerTitle className='text-lg font-bold'>
              {canSwitchBranch ? tHeader("switchTitle") : t("switchWorkspace")}
            </DrawerTitle>
            <DrawerDescription>{t("dialogDescription")}</DrawerDescription>
          </DrawerHeader>
          <div className='flex flex-col gap-5 overflow-y-auto overscroll-contain [&>*]:shrink-0 px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))]'>
            {canSwitchBranch ? (
              <section className='flex flex-col gap-2'>
                <SectionTitle>{tHeader("sectionBranches")}</SectionTitle>
                <ul className='divide-y divide-border/60 overflow-hidden rounded-2xl bg-muted/60'>
                  {orderedBranchOptions.map((option) => {
                    const isOrgScope = isParishBranchAlias(option.value)
                    const Icon = isOrgScope ? Building2Icon : GitBranchIcon
                    const selected = option.value === branchSlug
                    return (
                      <li key={option.value}>
                        <button
                          type='button'
                          aria-current={selected ? "true" : undefined}
                          onClick={() => choose(() => switchBranch(option.value))}
                          className={cn(rowClassName, selected ? "font-bold text-primary" : "font-medium text-foreground")}
                        >
                          <Icon aria-hidden className='size-5 shrink-0 text-muted-foreground' />
                          <span className='min-w-0 flex-1 truncate'>{isOrgScope ? t("organizationScope") : option.label}</span>
                          {selected ? <CheckIcon aria-hidden className='size-5 shrink-0' /> : null}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </section>
            ) : null}

            <section className='flex flex-col gap-2'>
              <SectionTitle>{tHeader("sectionChurches")}</SectionTitle>
              <ul className='divide-y divide-border/60 overflow-hidden rounded-2xl bg-muted/60'>
                {workspaceOptions.map((option) => {
                  const selected = option.value === workspaceSlug
                  return (
                    <li key={option.value}>
                      <button
                        type='button'
                        aria-current={selected ? "true" : undefined}
                        onClick={() => choose(() => switchWorkspace(option.value))}
                        className={cn(rowClassName, "text-foreground")}
                      >
                        <WorkspaceAvatar name={option.label} className='shrink-0' />
                        <span className={cn("min-w-0 flex-1 truncate", selected ? "font-bold text-primary" : "font-medium")}>
                          {option.label}
                        </span>
                        {selected ? (
                          <span className='flex shrink-0 items-center gap-1 text-xs font-bold text-primary'>
                            <CheckIcon aria-hidden className='size-4' />
                            {t("current")}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </section>

            {/* Registering another church lives only here, under the churches, where "which church" is the question. */}
            <button
              type='button'
              onClick={() => choose(() => setRegisterChurchOpen(true))}
              className='flex w-full items-center gap-3 rounded-2xl border border-border px-3 py-3 text-left transition-colors active:bg-muted'
            >
              <span className='bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-xl'>
                <PlusIcon aria-hidden className='size-4' />
              </span>
              <span className='flex min-w-0 flex-1 flex-col leading-tight'>
                <span className='text-sm font-bold text-foreground'>{t("registerChurch")}</span>
                <span className='text-xs text-muted-foreground'>{t("registerChurchHint")}</span>
                <span className='mt-1 flex items-center gap-1.5 text-xs font-semibold text-foreground'>
                  <AlertTriangleIcon aria-hidden className='size-3.5 shrink-0 text-destructive' />
                  {t("registerChurchWarning")}
                </span>
              </span>
              <ChevronRightIcon aria-hidden className='size-4 shrink-0 text-muted-foreground' />
            </button>
          </div>
        </DrawerContent>
      </Drawer>
      <RegisterChurchDialog open={registerChurchOpen} onOpenChange={setRegisterChurchOpen} />
    </>
  )

  return { open, element }
}
